import { useEffect, useState } from "react";

/**
 * 服务器时间校准：收到任何带 server_time 的快照时调用 calibrate，页面用 useNow() 取得校准后的“现在”。
 * 客户端时间只用于呈现插值；结算、到达、金币永远以服务端为准。
 * 开发库的世界时钟会被“跳”到别的时间、或按倍速快进（2026-09-26）：每次校准都按最新的回包重算偏移；
 * 偏移一变（超过 NOTIFY_MS），正在用 useNow 的页面立刻重读，不等下一拍。
 */
let offsetMs = 0;
const listeners = new Set<() => void>();
/** 偏移变动小于这么多不通知：同一台服务器、网络快慢带来的几十毫秒抖动，不值得重画。 */
const NOTIFY_MS = 1000;

export function calibrate(serverTimeIso: string, receivedAtMs: number = Date.now()): void {
  const server = Date.parse(serverTimeIso);
  if (Number.isNaN(server)) return;
  const next = server - receivedAtMs;
  const moved = Math.abs(next - offsetMs) >= NOTIFY_MS;
  offsetMs = next;
  if (moved) for (const listener of [...listeners]) listener();
}

export function serverNow(): number {
  return Date.now() + offsetMs;
}

/** 每 intervalMs 刷新一次的服务器校准时间；标签页回到前台时、偏移被重新校准过时立即刷新。 */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(serverNow);
  useEffect(() => {
    const tick = () => setNow(serverNow());
    const timer = window.setInterval(tick, intervalMs);
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    listeners.add(tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      listeners.delete(tick);
    };
  }, [intervalMs]);
  return now;
}

/**
 * 一段时长：四舍五入到分钟，不到 60 分钟“N 分钟”、整点“N 小时”、其余“N 小时 M 分”。
 * 数字和它的单位之间是不断行空格（U+00A0，看上去、复制出来、读屏读出来都是空格；2026-09-25）：窄屏不会折成“还要 50 / 分钟”“8 / 小时”；
 * 两组之间仍是普通空格，照常可以换行。旅途页、地图面板、行程卡、菜园都用它（地图面板原来自己再替换一遍，已删）。
 */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 60_000));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}\u00a0分钟`;
  return m === 0 ? `${h}\u00a0小时` : `${h}\u00a0小时 ${m}\u00a0分`;
}

export function formatClock(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** 按 IANA 时区显示当地时刻（例如出发地/目的地当地时间）。 */
export function formatLocalTime(iso: string, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat("zh-CN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone }).format(new Date(iso));
  } catch {
    return new Date(iso).toISOString().slice(11, 16);
  }
}
